/**
* PCC JSON-LD builders — LegalService + WebPage + FAQPage + HowTo +
* Service/Offer + BreadcrumbList shared across every /pcc/* route.
*/
import type { PccService, PccCity } from "@/data/pcc";
import type { PccPageContent } from "./generator";
import { SITE } from "@/config/sites";

export function buildPccGraph(args: {
  url: string;
  title: string;
  description: string;
  content: PccPageContent;
  service?: PccService;
  city?: PccCity;
  destination?: { slug: string; nameTh: string; nameEn: string };
  breadcrumb: Array<{ name: string; url: string }>;
}) {
  const host = SITE.canonicalHost ? `https://${SITE.canonicalHost}` : "";
  const org = {
    "@type": "LegalService",
    "@id": `${host}/#org`,
    name: SITE.name,
    url: host || undefined,
    areaServed: "TH",
    additionalType: "https://schema.org/GovernmentService",
  };
  const webPage = {
    "@type": "WebPage",
    "@id": args.url,
    url: args.url,
    name: args.title,
    description: args.description,
    inLanguage: "th-TH",
    isPartOf: { "@id": org["@id"] },
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", "h3", ".speakable-summary", ".lead", ".answer-summary", ".faq-answer", "[data-speakable]"] },
  };
  const faq = {
    "@type": "FAQPage",
    mainEntity: args.content.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const service = args.service ? {
    "@type": "Service",
    name: `Police Clearance Certificate — ${args.service.nameEn}`,
    serviceType: "Government liaison / Document procurement",
    provider: { "@id": org["@id"] },
    areaServed: args.city ? args.city.nameEn : "Thailand",
    audience: args.service.audience.join(", "),
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      deliveryLeadTime: {
        "@type": "QuantitativeValue",
        value: args.service.turnaroundDays,
        unitText: "business days",
      },
    },
  } : null;
  const howTo = {
    "@type": "HowTo",
    name: `วิธีขอใบตรวจประวัติอาชญากรรม (${args.service?.nameTh ?? "PCC"})`,
    step: args.content.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.tag,
      text: s.text,
    })),
  };
  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: args.breadcrumb.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url,
    })),
  };
  return {
    "@context": "https://schema.org",
    "@graph": [org, webPage, faq, ...(service ? [service] : []), howTo, breadcrumb],
  };
}
